/**
 * Resource aggregation across the portfolio.
 *
 * Decision: resources are matched by NAME (case-normalized, trimmed). This is
 * "good enough" for a demo and pre-launch reality. A formal resource ID model
 * is deferred to a future phase.
 *
 * Risks of the name-matching approach:
 *  - Two different people with the same name will be merged
 *  - Typos ("Sarah Johson" vs "Sarah Johnson") will look like two people
 *  - Renames break history
 * These are honestly surfaced in the UI ("Resource matching by name").
 */

import type {
  Project,
  Task,
  TeamMember,
  ResourceTimeOff,
  WorkingCalendar,
  WorkingCalendarException,
} from '@/types';
import { isWorkingDay, DEFAULT_WORKING_BITMASK } from '@/lib/workingCalendar';

export interface ResourceAssignment {
  /** Project this assignment belongs to */
  projectId: string;
  projectName: string;
  /** Task within the project (if assigned to a task) */
  taskId?: number;
  taskName?: string;
  taskStartDate?: string;
  taskEndDate?: string;
  taskPercentComplete?: number;
  /** Allocation percentage for this assignment (0-100) */
  allocation: number;
  /** Source of the assignment */
  source: 'team' | 'task';
  /** The role from the team list (only for team source) */
  role?: string;
  department?: string;
  email?: string;
}

export interface ResourceSummary {
  /** Normalized key (lowercase trimmed) used for matching */
  key: string;
  /** Display name (the most-frequently-used spelling) */
  displayName: string;
  /** All assignments across all projects */
  assignments: ResourceAssignment[];
  /** Distinct project IDs they're on */
  projectIds: string[];
  /** Total team-level allocation (sum of TeamMember.allocation across projects) */
  totalTeamAllocation: number;
  /** Roles they hold */
  roles: string[];
  /** Optional metadata pulled from team membership */
  email?: string;
  department?: string;
  /** Has any single date interval where allocation > 100%? */
  isOverAllocated: boolean;
  /** The maximum simultaneous allocation seen on any date */
  peakAllocation: number;
  /** Date interval(s) where over-allocated */
  conflictDays: number;
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The key that identifies a person in every calculation: their registry ID, or
 * their normalized name while no ID is known. Empty means no person, such as an
 * unfilled role. See docs/resources-unique-ids-spec.md.
 */
export function resourceKeyFor(id: string | null | undefined, name: string | null | undefined): string {
  return id || normalizeName(name ?? '');
}

/** Key of the person at this position in a task's resources. */
export function taskResourceKey(task: Pick<Task, 'resources' | 'resourceIds'>, index: number): string {
  return resourceKeyFor(task.resourceIds?.[index], task.resources?.[index]);
}

/** Key of a team member. Unfilled roles return an empty key. */
export function teamMemberKey(member: Pick<TeamMember, 'name' | 'resourceId'>): string {
  return member.name?.trim() ? resourceKeyFor(member.resourceId, member.name) : '';
}

export function timeOffKey(off: ResourceTimeOff): string {
  return resourceKeyFor(off.resourceId, off.nameNormalized || off.displayName);
}

/** The key for the person holding a role. */
export function roleAssignmentKey(a: { resourceId?: string | null; resourceName: string }): string {
  return resourceKeyFor(a.resourceId, a.resourceName);
}

/** The key for the person a skill level belongs to. */
export function skillLevelKey(l: { resourceId?: string | null; nameNormalized: string }): string {
  return resourceKeyFor(l.resourceId, l.nameNormalized);
}

/**
 * Step through every day in a date range and accumulate allocation per
 * resource per day. Used to detect cross-task and cross-project over-allocation.
 *
 * Returns Map<resource_key, Map<YYYY-MM-DD, total_allocation>>
 */
function buildDailyAllocation(
  resources: Map<string, ResourceSummary>,
): Map<string, Map<string, number>> {
  const dayMap = new Map<string, Map<string, number>>();

  for (const [key, summary] of resources) {
    const days = new Map<string, number>();
    for (const a of summary.assignments) {
      if (a.source !== 'task') continue;
      if (!a.taskStartDate || !a.taskEndDate) continue;
      // Skip already-completed tasks for current-state utilization
      if ((a.taskPercentComplete ?? 0) >= 100) continue;

      const start = new Date(a.taskStartDate);
      const end = new Date(a.taskEndDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;

      const cursor = new Date(start);
      // Cap loops to a sane horizon (1 year) to avoid runaway dates
      let safety = 0;
      while (cursor <= end && safety < 400) {
        const ymd = cursor.toISOString().split('T')[0];
        days.set(ymd, (days.get(ymd) ?? 0) + a.allocation);
        cursor.setDate(cursor.getDate() + 1);
        safety++;
      }
    }
    dayMap.set(key, days);
  }
  return dayMap;
}

/**
 * Aggregate resources across all projects.
 */
export function aggregateResources(projects: Project[]): ResourceSummary[] {
  // First pass: collect raw assignments and pick a display name
  const buckets = new Map<
    string,
    {
      displayNames: Map<string, number>;
      assignments: ResourceAssignment[];
      roles: Set<string>;
      email?: string;
      department?: string;
    }
  >();

  const ensure = (key: string, rawName: string) => {
    if (!key) return null;
    let b = buckets.get(key);
    if (!b) {
      b = {
        displayNames: new Map(),
        assignments: [],
        roles: new Set(),
      };
      buckets.set(key, b);
    }
    b.displayNames.set(rawName.trim(), (b.displayNames.get(rawName.trim()) ?? 0) + 1);
    return b;
  };

  for (const p of projects) {
    // Team-level entries
    for (const m of p.team ?? []) {
      const b = ensure(teamMemberKey(m), m.name);
      if (!b) continue;
      b.assignments.push({
        projectId: p.id,
        projectName: p.name,
        allocation: m.allocation,
        source: 'team',
        role: m.role,
        department: m.department,
        email: m.email,
      });
      if (m.role) b.roles.add(m.role);
      if (m.email) b.email = m.email;
      if (m.department) b.department = m.department;
    }

    // Task-level entries
    for (const t of p.wbsTasks ?? []) {
      if (t.type === 'summary') continue;
      const resources = t.resources ?? [];
      const allocations = t.allocation ?? [];
      for (let i = 0; i < resources.length; i++) {
        const name = resources[i];
        const alloc = allocations[i] ?? 100;
        const b = ensure(taskResourceKey(t, i), name);
        if (!b) continue;
        b.assignments.push({
          projectId: p.id,
          projectName: p.name,
          taskId: t.taskId,
          taskName: t.task,
          taskStartDate: t.startDate,
          taskEndDate: t.endDate,
          taskPercentComplete: t.percentComplete,
          allocation: alloc,
          source: 'task',
        });
      }
    }
  }

  // Build summaries
  const summaries = new Map<string, ResourceSummary>();
  for (const [key, b] of buckets) {
    const displayName =
      [...b.displayNames.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? key;
    const projectIds = [...new Set(b.assignments.map((a) => a.projectId))];
    const totalTeamAllocation = b.assignments
      .filter((a) => a.source === 'team')
      .reduce((s, a) => s + a.allocation, 0);

    summaries.set(key, {
      key,
      displayName,
      assignments: b.assignments,
      projectIds,
      totalTeamAllocation,
      roles: [...b.roles],
      email: b.email,
      department: b.department,
      isOverAllocated: false,
      peakAllocation: 0,
      conflictDays: 0,
    });
  }

  // Daily allocation pass to detect over-allocation conflicts
  const dailyMap = buildDailyAllocation(summaries);
  for (const [key, days] of dailyMap) {
    const summary = summaries.get(key);
    if (!summary) continue;
    let peak = 0;
    let conflictDays = 0;
    for (const alloc of days.values()) {
      if (alloc > peak) peak = alloc;
      if (alloc > 100) conflictDays++;
    }
    summary.peakAllocation = peak;
    summary.conflictDays = conflictDays;
    summary.isOverAllocated = peak > 100;
  }

  return [...summaries.values()].sort((a, b) => {
    // Most over-allocated first, then highest team allocation
    if (a.isOverAllocated !== b.isOverAllocated) return a.isOverAllocated ? -1 : 1;
    if (b.peakAllocation !== a.peakAllocation) return b.peakAllocation - a.peakAllocation;
    return a.displayName.localeCompare(b.displayName);
  });
}

// =============================================================================
// Per-project resource utilization
// =============================================================================

export interface ProjectResource {
  /** Person's display name */
  displayName: string;
  /** Their key (for cross-project lookups) */
  key: string;
  /** Their team-membership info if they are on the project team */
  teamMember?: TeamMember;
  /** Tasks they're assigned to in this project */
  taskAssignments: {
    task: Task;
    allocation: number;
  }[];
  /** Total weighted hours estimate (sum of task days * allocation) */
  weightedDays: number;
  /** Number of tasks they're on */
  taskCount: number;
}

export function computeProjectResources(project: Project): ProjectResource[] {
  const map = new Map<string, ProjectResource>();

  // Seed with team members
  for (const m of project.team ?? []) {
    const key = teamMemberKey(m);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        displayName: m.name.trim(),
        key,
        teamMember: m,
        taskAssignments: [],
        weightedDays: 0,
        taskCount: 0,
      });
    } else {
      const existing = map.get(key)!;
      existing.teamMember = m;
    }
  }

  // Walk tasks
  for (const t of project.wbsTasks ?? []) {
    if (t.type === 'summary') continue;
    const resources = t.resources ?? [];
    const allocations = t.allocation ?? [];
    for (let i = 0; i < resources.length; i++) {
      const name = resources[i];
      const alloc = allocations[i] ?? 100;
      const key = taskResourceKey(t, i);
      if (!key) continue;
      let pr = map.get(key);
      if (!pr) {
        pr = {
          displayName: name.trim(),
          key,
          taskAssignments: [],
          weightedDays: 0,
          taskCount: 0,
        };
        map.set(key, pr);
      }
      pr.taskAssignments.push({ task: t, allocation: alloc });
      pr.taskCount++;
      pr.weightedDays += (t.duration ?? 0) * (alloc / 100);
    }
  }

  return [...map.values()].sort((a, b) => {
    // Team members first, then by weighted days
    const aIsTeam = a.teamMember ? 1 : 0;
    const bIsTeam = b.teamMember ? 1 : 0;
    if (aIsTeam !== bIsTeam) return bIsTeam - aIsTeam;
    return b.weightedDays - a.weightedDays;
  });
}

// ===========================================================================
// Phase 30.20: Resource Heat Map aggregation
// ===========================================================================

/**
 * Normalize a name the same way internal aggregation does. Exposed so the
 * heat map can match capacity-registry rows against resource keys.
 */
export function normalizeResourceName(name: string): string {
  return normalizeName(name);
}

export type HeatmapGranularity = 'weekly' | 'monthly';

export interface HeatmapBucket {
  /** ISO yyyy-mm-dd (Monday for weekly, 1st-of-month for monthly). */
  start: string;
  /** ISO yyyy-mm-dd inclusive end. */
  end: string;
  /** Display label, e.g. "Jun 16" (weekly) or "Jun 2026" (monthly). */
  label: string;
  /** True if today falls inside [start, end]. */
  isCurrent: boolean;
}

export interface HeatmapResourceRow {
  /** name_normalized key. */
  key: string;
  displayName: string;
  /** Project ids this resource touches. */
  projectIds: string[];
  /** Effective hours/week capacity. Defaults to 40 if no registry entry. */
  capacityHoursPerWeek: number;
  /** True when no explicit capacity row exists. */
  capacityIsDefault: boolean;
  /** Per-bucket utilization values aligned with the heat map columns. */
  cells: HeatmapCell[];
  /** Peak utilization observed across the visible window. */
  peakUtilization: number;
}

export interface HeatmapCell {
  /** Allocation % summed across tasks, normalized to a 40-hour standard week.
   *  E.g. 1.5 = the resource has 60 hrs of work demanded that bucket. */
  rawAllocation: number;
  /** Utilization vs the resource's actual capacity:
   *  rawAllocation * (40 / capacityHoursPerWeek) * 100, expressed as a percentage. */
  utilizationPct: number;
  /** True iff utilizationPct > 100. */
  isOverAllocated: boolean;
}

export interface HeatmapData {
  granularity: HeatmapGranularity;
  buckets: HeatmapBucket[];
  rows: HeatmapResourceRow[];
}

function startOfWeekMonday(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0 = Sun
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  return x;
}

function startOfMonth(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function ymd(d: Date): string {
  return d.toISOString().split('T')[0];
}

function fmtMonth(d: Date): string {
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

function fmtWeek(d: Date): string {
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Build a window of buckets covering [windowStart, windowEnd]. For weekly,
 * the first bucket starts on the Monday <= windowStart. For monthly, the
 * first bucket starts on the 1st <= windowStart.
 */
function buildBuckets(
  windowStart: Date,
  windowEnd: Date,
  granularity: HeatmapGranularity,
): HeatmapBucket[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const out: HeatmapBucket[] = [];
  let cursor: Date;
  if (granularity === 'weekly') {
    cursor = startOfWeekMonday(windowStart);
  } else {
    cursor = startOfMonth(windowStart);
  }
  let safety = 0;
  while (cursor <= windowEnd && safety < 520) { // cap ~10 years weekly
    const start = new Date(cursor);
    const end = new Date(cursor);
    if (granularity === 'weekly') {
      end.setDate(end.getDate() + 6);
    } else {
      end.setMonth(end.getMonth() + 1);
      end.setDate(0); // last day of starting month
    }
    end.setHours(23, 59, 59, 999);
    const isCurrent = today >= start && today <= end;
    out.push({
      start: ymd(start),
      end: ymd(end),
      label: granularity === 'weekly' ? fmtWeek(start) : fmtMonth(start),
      isCurrent,
    });
    if (granularity === 'weekly') {
      cursor.setDate(cursor.getDate() + 7);
    } else {
      cursor.setMonth(cursor.getMonth() + 1);
      cursor.setDate(1);
    }
    safety++;
  }
  return out;
}

/**
 * Phase 30.20: aggregate task assignments across projects into a resource × time
 * heat map. The math:
 *
 *   For each resource, sum the daily allocation % from the existing aggregator.
 *   Then group days into buckets (weekly Mon-Sun or monthly 1st-end).
 *
 *   For each bucket:
 *     rawAllocation     = SUM(daily allocation %) / days_in_bucket
 *                         (avg daily allocation, in units where 100% = full
 *                          40-hour week)
 *     utilizationPct    = rawAllocation × (40 / capacity_hours) × 100
 *                         (so a part-time 20 hrs/wk person at 100% allocation
 *                          shows as 200% utilization → over-allocated)
 *
 * The window auto-fits to the earliest task start and latest task end across
 * all incoming projects, clamped so weekly view shows at most ~26 weeks and
 * monthly view shows at most ~24 months (otherwise the grid gets unreadable).
 */
/**
 * Fraction of a bucket's normal working capacity a person actually has, after
 * company holidays (working-calendar exceptions) and their own time-off.
 * 1 = full capacity; 0.5 = half the normal working days are unavailable.
 * Utilisation is divided by this, so lost capacity pushes utilisation up.
 */
function bucketAvailabilityFactor(
  bucketStart: string,
  bucketEnd: string,
  offRanges: { start: string; end: string }[],
  calendar: WorkingCalendar | null,
  exceptions: WorkingCalendarException[],
): number {
  const bitmask = calendar?.workingDaysBitmask ?? DEFAULT_WORKING_BITMASK;
  const cursor = new Date(bucketStart);
  const end = new Date(bucketEnd);
  let normalWorkingDays = 0;
  let availableDays = 0;
  let safety = 0;
  while (cursor <= end && safety < 400) {
    const isNormalWorkday = ((bitmask >> cursor.getDay()) & 1) === 1;
    if (isNormalWorkday) normalWorkingDays++;
    const iso = ymd(cursor);
    const works = isWorkingDay(iso, calendar, exceptions); // holiday-aware
    const off = offRanges.some((r) => iso >= r.start && iso <= r.end);
    if (works && !off) availableDays++;
    cursor.setDate(cursor.getDate() + 1);
    safety++;
  }
  if (normalWorkingDays <= 0) return 1;
  // Never above 1 (a weekend override isn't extra planned capacity); keep a
  // floor so a fully-unavailable person doesn't divide by zero.
  return Math.max(0.05, Math.min(1, availableDays / normalWorkingDays));
}

export function buildResourceHeatmap(
  projects: Project[],
  granularity: HeatmapGranularity,
  capacityMap: Record<string, { hoursPerWeek: number; displayName: string }>,
  options: {
    maxWeeklyBuckets?: number;
    maxMonthlyBuckets?: number;
    /** Phase: U7 — subtract these from available capacity. */
    timeOff?: ResourceTimeOff[];
    workingCalendar?: WorkingCalendar | null;
    workingCalendarExceptions?: WorkingCalendarException[];
  } = {},
): HeatmapData {
  const maxWeekly = options.maxWeeklyBuckets ?? 26;
  const maxMonthly = options.maxMonthlyBuckets ?? 24;

  // U7: time-off + holidays reduce available capacity. Build a per-person map of
  // off ranges; `applyAvailability` is false when there's nothing to subtract
  // (keeps behaviour identical for callers that don't pass calendar/time-off).
  const calendar = options.workingCalendar ?? null;
  const exceptions = options.workingCalendarExceptions ?? [];
  const offRangesByKey = new Map<string, { start: string; end: string }[]>();
  for (const off of options.timeOff ?? []) {
    const key = timeOffKey(off);
    if (!key || !off.startDate || !off.endDate) continue;
    let list = offRangesByKey.get(key);
    if (!list) { list = []; offRangesByKey.set(key, list); }
    list.push({ start: off.startDate, end: off.endDate });
  }
  const applyAvailability = exceptions.length > 0 || offRangesByKey.size > 0;

  // Re-use the existing aggregator's daily allocation. Easiest way to get it
  // is to call aggregateResources, which internally calls buildDailyAllocation
  // and stores nothing useful for our purposes — so we re-implement the
  // simpler daily walk here to avoid changing the existing public shape.
  //
  // Phase 30.20.3: two-pass discovery to align with the List tab. First pass
  // discovers ALL known resources (from team members and ALL task assignments,
  // including completed ones). Second pass attributes daily allocation only
  // from non-completed tasks. Net result: a team member with only completed
  // work shows in the grid with empty cells (signals "available capacity")
  // instead of disappearing entirely.
  const dailyByResource = new Map<string, Map<string, number>>();
  const displayByKey = new Map<string, string>();
  const projectsByKey = new Map<string, Set<string>>();

  // Pass 1: discover ALL resources from team + all task assignments
  for (const p of projects) {
    // Team members
    for (const m of p.team ?? []) {
      if (!m?.name) continue;
      const key = teamMemberKey(m);
      if (!key) continue;
      if (!displayByKey.has(key)) displayByKey.set(key, m.name);
      let pset = projectsByKey.get(key);
      if (!pset) { pset = new Set(); projectsByKey.set(key, pset); }
      pset.add(p.id);
    }
    // Task-assigned resources (including completed tasks — for discovery only)
    for (const t of p.wbsTasks ?? []) {
      if (!t.resources) continue;
      for (let i = 0; i < t.resources.length; i++) {
        const raw = t.resources[i];
        if (!raw) continue;
        const key = taskResourceKey(t, i);
        if (!key) continue;
        if (!displayByKey.has(key)) displayByKey.set(key, raw);
        let pset = projectsByKey.get(key);
        if (!pset) { pset = new Set(); projectsByKey.set(key, pset); }
        pset.add(p.id);
      }
    }
  }

  // Pass 2: attribute daily allocation from non-completed tasks only
  for (const p of projects) {
    for (const t of p.wbsTasks ?? []) {
      if (!t.resources || !t.startDate || !t.endDate) continue;
      if ((t.percentComplete ?? 0) >= 100) continue;
      const start = new Date(t.startDate);
      const end = new Date(t.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
      for (let i = 0; i < t.resources.length; i++) {
        const raw = t.resources[i];
        if (!raw) continue;
        const key = taskResourceKey(t, i);
        if (!key) continue;
        const alloc = (t.allocation?.[i] ?? 100) / 100; // % -> proportion
        let days = dailyByResource.get(key);
        if (!days) { days = new Map(); dailyByResource.set(key, days); }
        const cursor = new Date(start);
        let safety = 0;
        while (cursor <= end && safety < 400) {
          const k = ymd(cursor);
          days.set(k, (days.get(k) ?? 0) + alloc);
          cursor.setDate(cursor.getDate() + 1);
          safety++;
        }
      }
    }
  }

  // Compute window
  let minDate: Date | null = null;
  let maxDate: Date | null = null;
  for (const days of dailyByResource.values()) {
    for (const k of days.keys()) {
      const d = new Date(k);
      if (!minDate || d < minDate) minDate = d;
      if (!maxDate || d > maxDate) maxDate = d;
    }
  }

  // Fall back to a today-centered window if nothing to show
  if (!minDate || !maxDate) {
    const today = new Date();
    minDate = new Date(today);
    maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + (granularity === 'weekly' ? 7 * 8 : 30 * 6));
  }

  // Phase 30.20.1: anchor the window to where the data actually lives. The
  // previous implementation anchored to today minus N buckets and then clamped
  // the end at maxDate — which produced an empty bucket array when all task
  // data was older than (today - lookback). The fix: pick the anchor based on
  // today's relationship to the data range.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxBuckets = granularity === 'weekly' ? maxWeekly : maxMonthly;

  let anchor: Date;
  if (today < minDate) {
    // All data is in the future — anchor at the earliest data point
    anchor = new Date(minDate);
  } else if (today > maxDate) {
    // All data is in the past — anchor at the latest data point
    anchor = new Date(maxDate);
  } else {
    // Today is inside the data range — anchor at today
    anchor = today;
  }

  // Look back ~2 buckets from anchor, extend forward up to maxBuckets total
  let windowStart = new Date(anchor);
  if (granularity === 'weekly') {
    windowStart.setDate(anchor.getDate() - 14); // 2 weeks before anchor
  } else {
    windowStart.setMonth(anchor.getMonth() - 2); // 2 months before anchor
  }
  if (windowStart < minDate) windowStart = new Date(minDate);

  let windowEnd = new Date(windowStart);
  if (granularity === 'weekly') {
    windowEnd.setDate(windowStart.getDate() + 7 * maxBuckets);
  } else {
    windowEnd.setMonth(windowStart.getMonth() + maxBuckets);
  }
  // Don't extend beyond maxDate + a small buffer (avoids long empty tail)
  const dataEndBuffer = new Date(maxDate);
  if (granularity === 'weekly') {
    dataEndBuffer.setDate(dataEndBuffer.getDate() + 14); // 2 weeks past data
  } else {
    dataEndBuffer.setMonth(dataEndBuffer.getMonth() + 2); // 2 months past data
  }
  if (windowEnd > dataEndBuffer) windowEnd = dataEndBuffer;

  // Safety: never let windowEnd be before windowStart (shouldn't happen with
  // the fixed anchoring above, but defensive — guarantees at least 1 bucket).
  if (windowEnd < windowStart) windowEnd = new Date(windowStart);

  const buckets = buildBuckets(windowStart, windowEnd, granularity);

  // Build rows — iterate ALL discovered resources, not just those with daily
  // entries. A resource with no current allocation gets a row with all-empty
  // cells (signals "available capacity" — useful for capacity planning).
  const rows: HeatmapResourceRow[] = [];
  for (const key of displayByKey.keys()) {
    const days = dailyByResource.get(key) ?? new Map<string, number>();
    const cap = capacityMap[key];
    const capacityHours = cap?.hoursPerWeek ?? 40;
    const capacityIsDefault = !cap;
    const displayName = cap?.displayName ?? displayByKey.get(key) ?? key;
    const cells: HeatmapCell[] = [];
    let peak = 0;
    for (const b of buckets) {
      const bs = new Date(b.start);
      const be = new Date(b.end);
      let sum = 0;
      let dayCount = 0;
      const cursor = new Date(bs);
      while (cursor <= be) {
        const k = ymd(cursor);
        sum += days.get(k) ?? 0;
        dayCount++;
        cursor.setDate(cursor.getDate() + 1);
      }
      const rawAllocation = dayCount > 0 ? sum / dayCount : 0;
      let utilizationPct = rawAllocation * (40 / capacityHours) * 100;
      // U7: scale up by lost capacity (holidays + this person's time-off). Only
      // when there's demand — someone on PTO with no work stays at 0%.
      if (applyAvailability && utilizationPct > 0) {
        const factor = bucketAvailabilityFactor(
          b.start,
          b.end,
          offRangesByKey.get(key) ?? [],
          calendar,
          exceptions,
        );
        utilizationPct = Math.min(999, utilizationPct / factor);
      }
      const isOver = utilizationPct > 100;
      if (utilizationPct > peak) peak = utilizationPct;
      cells.push({ rawAllocation, utilizationPct, isOverAllocated: isOver });
    }
    rows.push({
      key,
      displayName,
      projectIds: [...(projectsByKey.get(key) ?? [])],
      capacityHoursPerWeek: capacityHours,
      capacityIsDefault,
      cells,
      peakUtilization: peak,
    });
  }

  // Sort: over-allocated first (highest peak first), then alphabetical
  rows.sort((a, b) => {
    if (a.peakUtilization > 100 && b.peakUtilization <= 100) return -1;
    if (b.peakUtilization > 100 && a.peakUtilization <= 100) return 1;
    if (a.peakUtilization !== b.peakUtilization) return b.peakUtilization - a.peakUtilization;
    return a.displayName.localeCompare(b.displayName);
  });

  return { granularity, buckets, rows };
}

export interface CapacityDemandBucket {
  label: string;
  start: string;
  isCurrent: boolean;
  /** Total FTE-equivalent demand across all resources that bucket. */
  demandFte: number;
  /** Total available FTE capacity (part-time + time off accounted for). */
  capacityFte: number;
}

/**
 * Portfolio capacity vs. demand over time, derived from committed task
 * assignments (the same data as the heat map). Demand is summed FTE-equivalent
 * allocation; capacity is summed per-person FTE (hours/40) reduced by holidays
 * and time off. Over-capacity buckets are where demand > capacity.
 */
export function buildCapacityVsDemand(
  projects: Project[],
  granularity: HeatmapGranularity,
  capacityMap: Record<string, { hoursPerWeek: number; displayName: string }>,
  options: {
    maxWeeklyBuckets?: number;
    maxMonthlyBuckets?: number;
    timeOff?: ResourceTimeOff[];
    workingCalendar?: WorkingCalendar | null;
    workingCalendarExceptions?: WorkingCalendarException[];
  } = {},
): CapacityDemandBucket[] {
  const heat = buildResourceHeatmap(projects, granularity, capacityMap, options);
  const calendar = options.workingCalendar ?? null;
  const exceptions = options.workingCalendarExceptions ?? [];
  const offRangesByKey = new Map<string, { start: string; end: string }[]>();
  for (const off of options.timeOff ?? []) {
    const key = timeOffKey(off);
    if (!key || !off.startDate || !off.endDate) continue;
    let list = offRangesByKey.get(key);
    if (!list) { list = []; offRangesByKey.set(key, list); }
    list.push({ start: off.startDate, end: off.endDate });
  }
  const round1 = (n: number) => Math.round(n * 10) / 10;

  return heat.buckets.map((b, idx) => {
    let demandFte = 0;
    let capacityFte = 0;
    for (const row of heat.rows) {
      demandFte += row.cells[idx]?.rawAllocation ?? 0;
      const fte = (row.capacityHoursPerWeek || 40) / 40;
      const factor = bucketAvailabilityFactor(
        b.start,
        b.end,
        offRangesByKey.get(row.key) ?? [],
        calendar,
        exceptions,
      );
      capacityFte += fte * factor;
    }
    return {
      label: b.label,
      start: b.start,
      isCurrent: b.isCurrent,
      demandFte: round1(demandFte),
      capacityFte: round1(capacityFte),
    };
  });
}

export interface CellContributor {
  projectId: string;
  projectName: string;
  taskId: number;
  taskName: string;
  /** This assignment's allocation % on the task. */
  allocationPct: number;
}

/**
 * What makes up a single heatmap cell: the (non-completed) tasks that overlap the
 * bucket and assign the given resource, with each assignment's allocation %.
 * Computed on demand for the cell drill-down (keeps HeatmapCell lean).
 */
export function cellContributors(
  projects: Project[],
  resourceKey: string,
  bucketStart: string,
  bucketEnd: string,
): CellContributor[] {
  const out: CellContributor[] = [];
  for (const p of projects) {
    for (const t of p.wbsTasks ?? []) {
      if (!t.resources || !t.startDate || !t.endDate) continue;
      if ((t.percentComplete ?? 0) >= 100) continue;
      // Task must overlap the bucket window (ISO yyyy-mm-dd compares lexically).
      if (t.endDate < bucketStart || t.startDate > bucketEnd) continue;
      for (let i = 0; i < t.resources.length; i++) {
        const raw = t.resources[i];
        if (!raw || taskResourceKey(t, i) !== resourceKey) continue;
        out.push({
          projectId: p.id,
          projectName: p.name,
          taskId: t.taskId,
          taskName: t.task,
          allocationPct: t.allocation?.[i] ?? 100,
        });
      }
    }
  }
  return out.sort((a, b) => b.allocationPct - a.allocationPct);
}

// ===========================================================================
// Phase 30.21: decision-support helpers for the task assignment UX
// ===========================================================================

export interface ResourceLoadHint {
  /** Normalized name key (same as buildResourceHeatmap). */
  key: string;
  displayName: string;
  /** Effective hours/week from the capacity registry, or 40 if no row exists. */
  capacityHoursPerWeek: number;
  capacityIsDefault: boolean;
  /**
   * Current allocation as percent of own capacity. Computed across all
   * non-completed tasks intersecting "today through next 4 weeks" — i.e.
   * a near-term snapshot, not historical. Useful as a quick "is this person
   * available?" signal in the task assignment UI.
   */
  currentLoadPct: number;
  /** True iff currentLoadPct > 100. */
  isOverAllocated: boolean;
}

/**
 * Phase 30.21: compute a near-term load percentage for each known resource.
 * Used to decorate the resource picker in TaskEditor with "currently 80%
 * allocated" hints so PMs see the load BEFORE assigning more work.
 *
 * Returns one entry per resource discovered across team + tasks (matches
 * how buildResourceHeatmap discovers resources). Resources with no active
 * tasks get currentLoadPct=0.
 */
export function computeResourceLoadHints(
  projects: Project[],
  capacityMap: Record<string, { hoursPerWeek: number; displayName: string }>,
  options: { lookaheadDays?: number } = {},
): ResourceLoadHint[] {
  const lookahead = options.lookaheadDays ?? 28; // ~4 weeks
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const horizonEnd = new Date(today);
  horizonEnd.setDate(today.getDate() + lookahead);

  const discovered = new Map<string, string>(); // key -> displayName

  // Discover all resources (team + task-assigned)
  for (const p of projects) {
    for (const m of p.team ?? []) {
      if (!m?.name) continue;
      const key = teamMemberKey(m);
      if (!key) continue;
      if (!discovered.has(key)) discovered.set(key, m.name);
    }
    for (const t of p.wbsTasks ?? []) {
      (t.resources ?? []).forEach((raw, i) => {
        if (!raw) return;
        const key = taskResourceKey(t, i);
        if (key && !discovered.has(key)) discovered.set(key, raw);
      });
    }
  }

  // Sum allocation in the lookahead window for each resource
  const daySum = new Map<string, number>();
  const dayCount = new Map<string, number>();

  for (const p of projects) {
    for (const t of p.wbsTasks ?? []) {
      if (!t.resources || !t.startDate || !t.endDate) continue;
      if ((t.percentComplete ?? 0) >= 100) continue;
      const taskStart = new Date(t.startDate);
      const taskEnd = new Date(t.endDate);
      if (Number.isNaN(taskStart.getTime()) || Number.isNaN(taskEnd.getTime())) continue;

      // Intersect task range with [today, horizonEnd]
      const overlapStart = taskStart < today ? today : taskStart;
      const overlapEnd = taskEnd > horizonEnd ? horizonEnd : taskEnd;
      if (overlapStart > overlapEnd) continue;

      for (let i = 0; i < t.resources.length; i++) {
        const raw = t.resources[i];
        if (!raw) continue;
        const key = taskResourceKey(t, i);
        if (!key) continue;
        const alloc = (t.allocation?.[i] ?? 100) / 100;
        let totalDays = dayCount.get(key) ?? 0;
        let totalSum = daySum.get(key) ?? 0;
        const cursor = new Date(overlapStart);
        let safety = 0;
        while (cursor <= overlapEnd && safety < 60) {
          totalSum += alloc;
          totalDays++;
          cursor.setDate(cursor.getDate() + 1);
          safety++;
        }
        daySum.set(key, totalSum);
        dayCount.set(key, totalDays);
      }
    }
  }

  // For each resource, compute the average daily allocation across the
  // lookahead window (lookaheadDays + 1, since today is included), then
  // adjust for capacity to get utilizationPct.
  const totalWindowDays = lookahead + 1;
  const out: ResourceLoadHint[] = [];
  for (const [key, displayName] of discovered) {
    const cap = capacityMap[key];
    const capacityHours = cap?.hoursPerWeek ?? 40;
    const capacityIsDefault = !cap;
    const totalSum = daySum.get(key) ?? 0;
    // Average daily allocation across the full window (zeros for days with no work)
    const avgDaily = totalWindowDays > 0 ? totalSum / totalWindowDays : 0;
    const currentLoadPct = avgDaily * (40 / capacityHours) * 100;
    out.push({
      key,
      displayName: cap?.displayName ?? displayName,
      capacityHoursPerWeek: capacityHours,
      capacityIsDefault,
      currentLoadPct,
      isOverAllocated: currentLoadPct > 100,
    });
  }
  return out.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export interface AssignmentConflict {
  /** Name key (normalized). */
  key: string;
  displayName: string;
  /** The peak weekly utilization % the assignment would create. */
  projectedPeakPct: number;
  /** Week (ISO Monday date) where the peak occurs. */
  peakWeekStart: string;
}

/**
 * Phase 30.21: preview the over-allocation impact of saving a candidate task.
 * Walks the user's existing projects, replacing or adding the candidate task,
 * then bucketizes weekly load for each affected resource. Returns conflicts
 * for resources whose projected weekly load would exceed 100%.
 *
 * Used in TaskEditor.handleSave to surface a non-blocking warning so the PM
 * can decide to proceed or adjust before committing.
 */
export function previewAssignmentConflicts(
  projects: Project[],
  candidateProjectId: string,
  candidateTask: Task,
  capacityMap: Record<string, { hoursPerWeek: number; displayName: string }>,
): AssignmentConflict[] {
  // Build a hypothetical project list with the candidate task replacing/adding.
  const hypothetical = projects.map((p) => {
    if (p.id !== candidateProjectId) return p;
    const existing = p.wbsTasks ?? [];
    const has = existing.some((t) => t.taskId === candidateTask.taskId);
    const tasks = has
      ? existing.map((t) => (t.taskId === candidateTask.taskId ? candidateTask : t))
      : [...existing, candidateTask];
    return { ...p, wbsTasks: tasks };
  });

  // Only care about the resources that the candidate task touches
  const affected = new Set<string>();
  (candidateTask.resources ?? []).forEach((raw, i) => {
    if (!raw) return;
    const k = taskResourceKey(candidateTask, i);
    if (k) affected.add(k);
  });
  if (affected.size === 0) return [];

  // Build daily allocations across the hypothetical projects, but only for
  // the affected keys (cheap)
  const dailyByKey = new Map<string, Map<string, number>>();
  for (const p of hypothetical) {
    for (const t of p.wbsTasks ?? []) {
      if (!t.resources || !t.startDate || !t.endDate) continue;
      if ((t.percentComplete ?? 0) >= 100) continue;
      const start = new Date(t.startDate);
      const end = new Date(t.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
      for (let i = 0; i < t.resources.length; i++) {
        const raw = t.resources[i];
        if (!raw) continue;
        const key = taskResourceKey(t, i);
        if (!affected.has(key)) continue;
        const alloc = (t.allocation?.[i] ?? 100) / 100;
        let days = dailyByKey.get(key);
        if (!days) { days = new Map(); dailyByKey.set(key, days); }
        const cursor = new Date(start);
        let safety = 0;
        while (cursor <= end && safety < 400) {
          const k = ymd(cursor);
          days.set(k, (days.get(k) ?? 0) + alloc);
          cursor.setDate(cursor.getDate() + 1);
          safety++;
        }
      }
    }
  }

  // Restrict the check window to the candidate task's own date range
  const taskStart = new Date(candidateTask.startDate);
  const taskEnd = new Date(candidateTask.endDate);
  if (Number.isNaN(taskStart.getTime()) || Number.isNaN(taskEnd.getTime())) return [];

  const conflicts: AssignmentConflict[] = [];
  for (const key of affected) {
    const cap = capacityMap[key];
    const capacityHours = cap?.hoursPerWeek ?? 40;
    const days = dailyByKey.get(key);
    if (!days) continue;

    // Walk weekly buckets within [taskStart, taskEnd]
    const weekCursor = startOfWeekMonday(taskStart);
    let peakPct = 0;
    let peakWeek = ymd(weekCursor);
    let safety = 0;
    while (weekCursor <= taskEnd && safety < 200) {
      const weekEnd = new Date(weekCursor);
      weekEnd.setDate(weekEnd.getDate() + 6);
      let sum = 0;
      let count = 0;
      const day = new Date(weekCursor);
      while (day <= weekEnd && safety < 800) {
        const k = ymd(day);
        sum += days.get(k) ?? 0;
        count++;
        day.setDate(day.getDate() + 1);
        safety++;
      }
      const avgDaily = count > 0 ? sum / count : 0;
      const pct = avgDaily * (40 / capacityHours) * 100;
      if (pct > peakPct) {
        peakPct = pct;
        peakWeek = ymd(weekCursor);
      }
      weekCursor.setDate(weekCursor.getDate() + 7);
      safety++;
    }
    if (peakPct > 100) {
      conflicts.push({
        key,
        displayName: cap?.displayName ?? candidateTask.resources?.find((_r, i) => taskResourceKey(candidateTask, i) === key) ?? key,
        projectedPeakPct: peakPct,
        peakWeekStart: peakWeek,
      });
    }
  }
  return conflicts;
}
